import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  groupShiftsByPlatform,
  nextDayMergedByPlatform,
} from "@/utils/time";
import type { GroupedShifts, Shift } from "@/types";
import type { RootState } from "..";

interface ShiftsState {
  data: Shift[];
}

const initialState: ShiftsState = { data: [] };

const shiftsSlice = createSlice({
  name: "shifts",
  initialState,
  reducers: {
    setShifts(state, action: PayloadAction<Shift[] | null>) {
      state.data = action.payload ?? [];
    },
  },
});

export const { setShifts } = shiftsSlice.actions;

export interface FormattedShifts {
  formattedShifts: GroupedShifts;
  /**
   * Per platform, whether its trading day runs past midnight. The Store Hours
   * screen uses it to decide when "today" rolls over — see `nextDayMergedByPlatform`.
   */
  nextDayMergedByPlatform: Record<string, boolean>;
}

/**
 * `{ formattedShifts, nextDayMergedByPlatform }` — the original's `Kq`, consumed
 * by `useStoreStatus` and by the Store Hours screen.
 *
 * Memoised deliberately. As a plain function this built a fresh object on every
 * call, so `useSelector` saw a new reference each time and re-rendered every
 * subscriber on *any* store action.
 */
export const selectFormattedShifts = createSelector(
  [(state: RootState) => state.shifts.data],
  (shifts = []): FormattedShifts => ({
    formattedShifts: groupShiftsByPlatform(shifts),
    nextDayMergedByPlatform: nextDayMergedByPlatform(shifts),
  }),
);

export default shiftsSlice.reducer;
